import React, { Suspense, lazy, useEffect, useState } from "react";
import { COLORS, FONT_DISPLAY, FONT_MONO } from "./theme.js";
import { useAuth } from "./auth/AuthProvider.jsx";
import { useItems } from "./data/useItems.js";
import { useNotifications } from "./data/useNotifications.js";
import { useToast } from "./components/Toast.jsx";
import BottomNav from "./components/BottomNav.jsx";
import NotificationPanel from "./components/NotificationPanel.jsx";
import { LoadingPanel, ErrorNote, LogoMark } from "./components/ui.jsx";
import HomeDashboard from "./screens/HomeDashboard.jsx";
import PassportList from "./screens/PassportList.jsx";
import AddItemWizard from "./screens/AddItemWizard.jsx";
import ItemDetail from "./screens/ItemDetail.jsx";
import EditItem from "./screens/EditItem.jsx";
import LegacyTimeline from "./screens/LegacyTimeline.jsx";
import PreservationHub from "./screens/PreservationHub.jsx";
import Profile from "./screens/Profile.jsx";

/* Lazy: the community half pulls in its own data layer and a realtime
   subscription, and most sessions never leave the collection side. */
const BaggageClaim = lazy(() => import("./screens/BaggageClaim.jsx"));

/* The two charting screens pull in recharts, which is by far the heaviest
   dependency. Loading them on demand keeps the first paint — splash, login and
   the Passport itself — small on a phone. */
const FlightTracker = lazy(() => import("./screens/FlightTracker.jsx"));
const CollectionAnalytics = lazy(() => import("./screens/CollectionAnalytics.jsx"));

/* The signed-in application: five-tab shell plus three full-screen overlays
   (add wizard, item detail, analytics/timeline). */
export default function AppShell() {
  const { displayName, profile, user, avatarUrl, setAvatar, refreshAvatar, signOut } = useAuth();
  const showToast = useToast();
  const {
    items, photoUrls, loading, error, saveError, dismissSaveError, reload,
    addItem, updateItem, deleteItem, logWear, logAction, updateStatus, updateCondition,
    deleteHistoryEntry, updateHistoryEntry, addPhotos, removePhoto, refreshPhoto,
  } = useItems();

  /* Toasts confirm the small, tap-sized actions. They fire only after the write
     succeeded — a confirmation over a failed save is the thing this project has
     spent its time removing. */
  const logWearWithToast = async (id) => {
    const res = await logWear(id);
    if (res !== false) showToast("Flight logged");
  };
  const logActionWithToast = async (id, action) => {
    const res = await logAction(id, action);
    if (res !== false) showToast(action === "Cleaned" ? "Cleaning logged" : "Rotation logged");
  };
  const deleteHistoryWithToast = async (itemId, entryId) => {
    const { error: err } = await deleteHistoryEntry(itemId, entryId);
    if (!err) showToast("Entry removed");
  };
  /* The scanner has already shown the collector exactly what it proposes and
     they pressed the button, so the toast confirms the write, not the finding. */
  const applyScan = async (itemId, materials) => {
    const res = await updateItem(itemId, { materials });
    if (res.error) return res;
    /* Logged as history so the Intelligence Division badges have something real
       to count, and so a collector can see when a scan last touched an item. It
       is not a wear — the totals only count 'Worn'. A failure here must not
       undo the materials that already saved, so it is deliberately not awaited
       into the result. */
    logAction(itemId, "AI Scan");
    showToast("Materials updated");
    return res;
  };

  const [activeTab, setActiveTab] = useState("home");
  const [mode, setMode] = useState("terminal");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [overlay, setOverlay] = useState(null); // null | 'analytics' | 'timeline'
  const [bellOpen, setBellOpen] = useState(false);
  /* Set when a notification names a room, so Baggage Claim can open the
     conversation rather than the tab that lists it. Cleared once consumed, or
     a second notification for the same room would reopen it after a manual
     back-out. */
  const [pendingRoomId, setPendingRoomId] = useState(null);
  const { social, socialAvailable, careReminders, unreadCount, markRead, markAllRead } = useNotifications(items);

  /* Both kinds of notification land somewhere: a care reminder opens the item
     it is about, a message opens the room it came from. A notification that
     only dismisses itself is a worse version of a badge. */
  function openFromNotification(itemId) {
    setMode("terminal");
    setOverlay(null);
    setAdding(false);
    setEditingItemId(null);
    setSelectedItemId(itemId);
  }

  function openRoomFromNotification(roomId) {
    setMode("community");
    setPendingRoomId(roomId);
  }

  const selectedItem = items.find((i) => i.id === selectedItemId) || null;
  const editingItem = items.find((i) => i.id === editingItemId) || null;

  // If a reload drops the open item (deleted elsewhere, or a failed write rolled
  // back an optimistic insert), fall back to the list rather than a blank screen.
  useEffect(() => {
    if (selectedItemId && !loading && !selectedItem) setSelectedItemId(null);
  }, [selectedItemId, selectedItem, loading]);

  async function handleAdd(data) {
    const result = await addItem(data);
    if (!result.error) {
      setAdding(false);
      showToast("Item added to Passport");
    }
    return result;
  }

  async function handleSaveEdit(id, data) {
    const result = await updateItem(id, data);
    if (!result.error) setEditingItemId(null);
    return result;
  }

  // After deleting, close both the edit form and the detail view — the item
  // they were looking at no longer exists.
  async function handleDelete(id) {
    const result = await deleteItem(id);
    if (!result.error) {
      setEditingItemId(null);
      setSelectedItemId(null);
    }
    return result;
  }

  const showNav = mode === "terminal" && !adding && !overlay && !selectedItem && !editingItem;

  function renderBody() {
    if (loading) return <LoadingPanel label="Opening your Passport…" />;
    if (error) return <ErrorNote message={error} onRetry={reload} />;

    if (adding) return <AddItemWizard onAdd={handleAdd} onCancel={() => setAdding(false)} />;

    if (editingItem) {
      return (
        <EditItem
          item={editingItem}
          onSave={handleSaveEdit}
          onDelete={handleDelete}
          onCancel={() => setEditingItemId(null)}
        />
      );
    }

    if (overlay === "analytics") return <CollectionAnalytics items={items} onBack={() => setOverlay(null)} />;
    if (overlay === "timeline") {
      return (
        <>
          <button
            onClick={() => setOverlay(null)}
            style={{ background: "transparent", color: COLORS.creamDim, border: "none", cursor: "pointer", fontSize: 14, marginBottom: 14 }}
          >
            ← Back
          </button>
          <LegacyTimeline items={items} />
        </>
      );
    }

    if (selectedItem) {
      return (
        <ItemDetail
          onApplyScan={applyScan}
          item={selectedItem}
          photoUrls={photoUrls}
          onBack={() => setSelectedItemId(null)}
          onEdit={setEditingItemId}
          onLogWear={logWearWithToast}
          onUpdateCondition={updateCondition}
          onLogAction={logActionWithToast}
          onUpdateStatus={updateStatus}
          onAddPhotos={addPhotos}
          onDeletePhoto={removePhoto}
          onRefreshPhoto={refreshPhoto}
          onDeleteHistoryEntry={deleteHistoryWithToast}
          onUpdateHistoryEntry={updateHistoryEntry}
        />
      );
    }

    switch (activeTab) {
      case "passport":
        return (
          <PassportList
            items={items}
            onOpenItem={setSelectedItemId}
            onEditItem={setEditingItemId}
            onAdd={() => setAdding(true)}
          />
        );
      case "flight":
        return <FlightTracker items={items} onLogWear={logWearWithToast} />;
      case "hub":
        return <PreservationHub />;
      case "profile":
        return (
          <Profile
            items={items}
            displayName={displayName}
            profile={profile}
            email={user?.email}
            avatarUrl={avatarUrl}
            onSetAvatar={setAvatar}
            onRefreshAvatar={refreshAvatar}
            onOpenAnalytics={() => setOverlay("analytics")}
            onSignOut={signOut}
          />
        );
      default:
        return (
          <HomeDashboard
            items={items}
            displayName={displayName}
            onOpenAnalytics={() => setOverlay("analytics")}
            onOpenItem={setSelectedItemId}
            onGoTab={setActiveTab}
            onOpenTimeline={() => setOverlay("timeline")}
          />
        );
    }
  }

  return (
    <>
      {/* The mark sits above every screen, outside the scroll area, so it stays
          put while the content moves under it. */}
      <header
        style={{
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 9,
          padding: "12px 18px 10px",
          paddingTop: "calc(12px + env(safe-area-inset-top))",
          borderBottom: `1px solid ${COLORS.lineFaint}`,
          background: COLORS.black,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <LogoMark size={22} />
          <span
            style={{
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: 15,
              letterSpacing: 2.4,
              color: COLORS.goldBright,
            }}
          >
            SENOGARA
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => setBellOpen((open) => !open)}
            aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} waiting` : "Notifications"}
            style={{ position: "relative", background: "none", border: "none", cursor: "pointer", fontSize: 17, padding: 4, lineHeight: 1 }}
          >
            🔔
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", top: -1, right: -1,
                  background: COLORS.red, color: COLORS.cream,
                  fontSize: 9, fontFamily: FONT_MONO, fontWeight: 700,
                  borderRadius: 10, minWidth: 15, height: 15,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px",
                }}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Two halves of the product, not two tabs: your own collection, and
              everyone else's. A sixth nav slot would have buried the second. */}
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            aria-label="Switch between your collection and the community"
            style={{
              background: COLORS.blackCard2, color: COLORS.goldBright,
              border: `1px solid ${COLORS.gold}`, borderRadius: 20,
              padding: "5px 10px", fontFamily: FONT_MONO, fontSize: 11,
              fontWeight: 700, cursor: "pointer",
            }}
          >
            <option value="terminal">✈️ My Terminal</option>
            <option value="community">🧳 Baggage Claim</option>
          </select>
        </div>
      </header>

      {bellOpen && (
        <NotificationPanel
          social={social}
          socialAvailable={socialAvailable}
          careReminders={careReminders}
          unreadCount={unreadCount}
          onClose={() => setBellOpen(false)}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onOpenItem={openFromNotification}
          onOpenRoom={openRoomFromNotification}
        />
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: mode === "community" ? "hidden" : "auto",
          display: mode === "community" ? "flex" : "block",
          flexDirection: "column",
          padding: "20px 18px 8px",
          paddingBottom: mode === "community" ? "calc(12px + env(safe-area-inset-bottom))" : 8,
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* A failed write is shown above whatever screen the user is on, since
            the optimistic change has already been rolled back underneath them. */}
        {saveError && (
          <div style={{ marginBottom: 14 }}>
            <ErrorNote message={saveError} onRetry={dismissSaveError} actionLabel="Dismiss" />
          </div>
        )}
        <Suspense fallback={<LoadingPanel />}>
          {mode === "community" ? (
            <BaggageClaim openRoomId={pendingRoomId} onRoomOpened={() => setPendingRoomId(null)} />
          ) : (
            renderBody()
          )}
        </Suspense>
      </div>
      {showNav && <BottomNav active={activeTab} onChange={setActiveTab} />}
    </>
  );
}
